import React, {useEffect} from 'react';
import {Grid, Box} from '@mui/material';
import {useTranslation} from 'react-i18next';

import Dialog from 'components/Dialog';
import Panel from 'components/Panel';
import Formik from 'components/hook-form/Formik';
import FormikTextField from 'components/inputs/FormikTextField';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import AsyncContainer from 'components/AsyncContainer';

import {DialogTypes} from 'common/names';
import {isEmpty} from 'lodash';
import {
   ISamlSettings,
   SamlSettingsNames,
   SamlSettingsSchema,
} from 'types/diplomat/settings/SamlSettings';
import {usePostApi} from 'common/hooks/usePostApi';
import api from 'api';
import {castToSend} from 'common/yup/castToSend';
import {useGetApi} from 'common/hooks/useGetApi';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';
import FormikDropdown from 'components/inputs/FormikDropdown';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import {useSelector} from 'react-redux';
import {layoutSelectors} from 'features/layout/redux';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import {ReadOnlyProvider} from 'common/context/ReadOnlyContext';

type DialogForm = {
   ok: (values: ISamlSettings, callbacks: any) => void;
   cancel: () => void;
};

const SettingsSAMLForm = ({cancel}: {cancel: DialogForm['cancel']}) => {
   const {t} = useTranslation();

   const getValue = useFieldValueGetter<ISamlSettings>();
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');
   const enabled = getValue(SamlSettingsNames.enabled);

   const certs = useSelector(layoutSelectors.serverSslKeys);

   return (
      <>
         <Panel name={t('SAML SSO Settings')}>
            <Grid container spacing={2}>
               <Grid item xs={12}>
                  <FormikCheckbox name={SamlSettingsNames.enabled} label={t('Enabled')} />
               </Grid>
               <ReadOnlyProvider value={!enabled}>
                  <>
                     <Grid item xs={12}>
                        <FormikTextField
                           name={SamlSettingsNames.spEntityId}
                           label={t('SP Entity ID')}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <FormikTextField
                           name={SamlSettingsNames.idpEntityId}
                           label={t('IdP Entity ID')}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <FormikTextField
                           name={SamlSettingsNames.idpSingleSignOnServiceUrl}
                           label={t('IdP SSO Sign On URL')}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <FormikDropdown
                           name={SamlSettingsNames.idpX509CertificateId}
                           label={t('IdP Server Certificate')}
                           options={certs}
                        />
                     </Grid>
                  </>
               </ReadOnlyProvider>
            </Grid>
         </Panel>

         <LastUpdatedLabel date={timestamp} author={lastUser} />

         <Box textAlign="center">
            <FormFooterButtons cancel={cancel} />
         </Box>
      </>
   );
};

const SettingsSAMLFormWrapper = ({ok, cancel}: DialogForm) => {
   const {state: settings, request} = useGetApi<ISamlSettings>(api.samlSettings);

   useEffect(request, [request]);

   return (
      <AsyncContainer loading={isEmpty(settings)}>
         <Formik
            initialValues={SamlSettingsSchema.cast(settings)}
            validationSchema={SamlSettingsSchema}
            onSubmit={ok}
            enableReinitialize
         >
            <SettingsSAMLForm cancel={cancel} />
         </Formik>
      </AsyncContainer>
   );
};

type DialogProps = {
   onClose: () => void;
};

export const SamlSettingsDialog = ({onClose}: DialogProps) => {
   const {request: sendData} = usePostApi(api.samlSettings);

   return (
      <Dialog maxWidth="md" header={DialogTypes.SettingsSAMLDialog.dialogName} close={onClose}>
         <SettingsSAMLFormWrapper
            ok={(values, callbacks) => {
               sendData(JSON.stringify(castToSend(SamlSettingsSchema, values)), callbacks);
               onClose();
            }}
            cancel={onClose}
         />
      </Dialog>
   );
};

export default SamlSettingsDialog;
